// Global cache to store multiple rating data when it's encountered in other components
let multipleRatingDataCache = {};

/**
 * Store multiple rating data so it can be accessed by the prompt generator
 * @param {string} eventId - The ID of the event
 * @param {string} ideaId - The ID of the rated item (idea)
 * @param {Array} ratingDistribution - The rating distribution data
 * @param {Object} metrics - Additional metrics (mean, median, etc.)
 */
export const storeMultipleRatingData = (eventId, ideaId, ratingDistribution, metrics = {}) => {
  if (!eventId || !ideaId || !ratingDistribution || !Array.isArray(ratingDistribution)) {
    console.warn('⚠️ Invalid data passed to storeMultipleRatingData', { 
      eventId, 
      ideaId,
      hasDistribution: !!ratingDistribution, 
      isArray: Array.isArray(ratingDistribution) 
    });
    return;
  }
  
  console.log('📥 Storing multiple rating data for item:', { 
    eventId, 
    ideaId,
    distributionLength: ratingDistribution.length, 
    metrics,
    timestamp: new Date().toISOString() 
  });
  
  // Initialize event data object if it doesn't exist
  if (!multipleRatingDataCache[eventId]) {
    multipleRatingDataCache[eventId] = {};
  }
  
  // Store data for this specific item
  multipleRatingDataCache[eventId][ideaId] = {
    distribution: ratingDistribution,
    metrics: metrics
  };
};

/**
 * Get stored multiple rating data for an event
 * @param {string} eventId - The ID of the event
 * @returns {Object|null} The multiple rating data or null if not found
 */
export const getStoredMultipleRatingData = (eventId) => {
  if (!eventId || !multipleRatingDataCache[eventId]) {
    return null;
  }
  return multipleRatingDataCache[eventId];
};

/**
 * Clear the multiple rating data cache
 */
export const clearMultipleRatingDataCache = () => {
  multipleRatingDataCache = {};
  console.log('🧺 Multiple rating data cache cleared');
};

/**
 * Fallback function in case of errors
 * @returns {string} A fallback prompt for multiple rating analysis
 */
export const getMultipleRatingFallbackPrompt = () => {
  return `You are an expert data analyst analyzing a Survey with Multiple Rating questions. This type of survey asks respondents to rate multiple items on a numerical scale (typically 1-5 stars).\n\nDue to limited data availability, please provide general analysis principles for interpreting multiple rating survey data, including:\n\n1. How to compare ratings across multiple items\n2. Best practices for analyzing rating distributions\n3. Methods for identifying strengths and weaknesses from rating patterns\n4. Techniques for converting multiple ratings into actionable insights\n5. How to communicate rating-based results effectively\n\nStructure your response with these EXACT section headings using single # for each section:\n\n# Executive Summary\n# Key Themes\n# Recommendations\n# Limitations\n# Survey Design Feedback`;
};

export const generateMultipleRatingPrompt = (eventDetails, stats, posts = []) => {
  console.log('⚙️ generateMultipleRatingPrompt called with:', {
    hasEventDetails: !!eventDetails,
    eventDetailsKeys: eventDetails ? Object.keys(eventDetails) : [],
    hasStats: !!stats,
    statsKeys: stats ? Object.keys(stats) : [],
    hasPosts: !!posts,
    postsCount: Array.isArray(posts) ? posts.length : 0,
    timestamp: new Date().toISOString()
  });

  if (!eventDetails) {
    console.error('❌ Missing eventDetails in generateMultipleRatingPrompt');
    return null;
  }  
  if (!stats) {
    console.warn('⚠️ Missing stats in generateMultipleRatingPrompt, continuing with limited data');
  }  try {
    // Format event details and stats for the prompt
    const eventName = eventDetails?.name || 'N/A';
    const questionType = eventDetails?.question_type || 'multiple_rating';
    const description = eventDetails?.description || 'N/A';
    
    // Access metrics data from the correct path
    const metricsData = stats?.metrics || {};
    const summaryData = stats?.summary || {};
    
    console.log('📊 MultipleRating prompt - accessing metrics:', { 
      hasMetrics: !!metricsData, 
      metricsDataKeys: Object.keys(metricsData),
      hasSummary: !!summaryData,
      summaryDataKeys: Object.keys(summaryData),
      timestamp: new Date().toISOString() 
    });
    
    // Extract total responses with exhaustive fallback paths
    let totalResponses = 0;
    
    // Try all possible paths for total responses in metrics
    if (metricsData && typeof metricsData === 'object') {
      totalResponses = metricsData.contributed || metricsData.choices || metricsData.responses || 
                      metricsData.ideas || metricsData.totalResponses || metricsData.total || 
                      metricsData.total_vote_creator || metricsData.idea_creator || 0;
    }    
    // If summary has total responses, use that as a fallback
    if (totalResponses === 0 && summaryData && typeof summaryData === 'object') {
      totalResponses = summaryData.total_choices || summaryData.total_responses || 
                     summaryData.responses || summaryData.total || summaryData.contributed || 0;
    }    
    // Check the top level stats object as a last resort
    if (totalResponses === 0) {
      totalResponses = stats.total_choices || stats.total_responses || stats.responses || 
                     stats.choices || stats.ideas || stats.contributed || stats.total || 0;
    }    
    console.log('🔎 Multiple rating - complete search for totalResponses in all locations:', {
      final_totalResponses: totalResponses, 
      metricsData_paths: metricsData ? {
        contributed: metricsData.contributed,
        choices: metricsData.choices,
        responses: metricsData.responses,
        ideas: metricsData.ideas,
        total_vote_creator: metricsData.total_vote_creator
      } : 'No metrics data',
      summaryData_paths: summaryData ? {
        total_choices: summaryData.total_choices,
        total_responses: summaryData.total_responses,
        responses: summaryData.responses
      } : 'No summary data',
      topLevel_paths: {
        total_choices: stats.total_choices,
        total_responses: stats.total_responses,
        choices: stats.choices
      },
      timestamp: new Date().toISOString()
    });
    
    // Extract total users with enhanced fallbacks
    let totalUsers = 0;
    
    // Try all possible paths for totalUsers in metrics
    if (metricsData && typeof metricsData === 'object') {
      totalUsers = metricsData.total_user || metricsData.total_users || metricsData.viewed_user || 
                  metricsData.joined || metricsData.participants || metricsData.contributors || 0;
    }    
    // Check summary data for user counts
    if (totalUsers === 0 && summaryData && typeof summaryData === 'object') {
      totalUsers = summaryData.total_user || summaryData.total_users || summaryData.users || 
                 summaryData.participants || summaryData.contributors || summaryData.joined || 0;
    }    
    // Check top level stats as a last resort
    if (totalUsers === 0) {
      totalUsers = stats.total_user || stats.total_users || stats.users || 
                 stats.participants || stats.joined || stats.viewers || 0;
    }    
    // If we have response data but no user count, estimate conservatively
    if (totalUsers === 0 && totalResponses > 0) {
      totalUsers = Math.max(Math.ceil(totalResponses * 1.2), totalResponses + 3);
      console.log('⚠️ Multiple rating - estimated totalUsers:', totalUsers);
    }    
    // Extract average score with enhanced validation
    let averageScore = null;
    
    // Try to find the average score in all possible locations
    const possibleScorePaths = [
      summaryData?.simple_score,
      summaryData?.average_score,
      summaryData?.avg_score,
      summaryData?.score,
      metricsData?.simple_score,
      metricsData?.average_score,
      metricsData?.avg_score,
      metricsData?.score,
      stats?.simple_score,
      stats?.average_score,
      stats?.avg_score,
      stats?.score
    ];
    
    // Find the first non-null, non-undefined score
    for (const path of possibleScorePaths) {
      if (path !== undefined && path !== null) {
        averageScore = parseFloat(path);
        if (!isNaN(averageScore)) {
          break;
        }      }    }    
    console.log('📊 Multiple rating - extracted averageScore:', { 
      averageScore, 
      possiblePaths: {
        'summaryData.simple_score': summaryData?.simple_score,
        'summaryData.average_score': summaryData?.average_score,
        'metricsData.average_score': metricsData?.average_score
      }    });
    
    // Calculate participation rate with validation
    let participationRate = 0;
    
    // Try to get it directly from metrics or summary first
    if (metricsData?.participation_rate !== undefined && metricsData?.participation_rate !== null) {
      participationRate = metricsData.participation_rate;
    } else if (summaryData?.participation_rate !== undefined && summaryData?.participation_rate !== null) {
      participationRate = summaryData.participation_rate;
    } else if (stats?.participation_rate !== undefined && stats?.participation_rate !== null) {
      participationRate = stats.participation_rate;
    }    // Otherwise calculate it if we have valid totalUsers
    else if (totalUsers > 0) {
      participationRate = Math.round((totalResponses / totalUsers) * 100);
    }    
    // Validate participation rate is realistic (cannot be > 100%)
    if (participationRate > 100) {
      console.log(`⚠️ Multiple rating - participation rate over 100%: ${participationRate}%, capping at 100%`);
      participationRate = 100;
    }    
    console.log('📊 Multiple rating - metrics extraction results:', {
      totalResponses,
      totalUsers,
      participationRate,
      averageScore,
      timestamp: new Date().toISOString()
    });
    
    console.log('📊 Multiple Rating prompt metrics extraction:', { 
      totalResponses, 
      totalUsers, 
      participationRate, 
      timestamp: new Date().toISOString() 
    });
    
    // Check for cached multiple rating data
    const currentEventId = eventDetails.id || eventDetails.eventId;
    const cachedRatingData = getStoredMultipleRatingData(currentEventId);
    
    console.log('🔍 Checking cached multiple rating data:', { 
      eventId: currentEventId, 
      hasCachedData: !!cachedRatingData,
      itemCount: cachedRatingData ? Object.keys(cachedRatingData).length : 0,
      timestamp: new Date().toISOString() 
    });
    
    // Format rated items data using the cached data
    let ratedItems = [];
    
    if (cachedRatingData && Object.keys(cachedRatingData).length > 0) {
      console.log('✅ Using cached multiple rating data');
    
    // Convert the object to an array for processing
    ratedItems = Object.entries(cachedRatingData).map(([ideaId, data]) => {
      const { distribution, metrics } = data;
      
      // Calculate percentages for each star rating
      let formattedDistribution = {};
      if (Array.isArray(distribution)) {
        distribution.forEach(item => {
          const rating = item.value || parseInt(item.label) || 0;
          formattedDistribution[rating] = {
            count: item.count || 0,
            percentage: item.pct || '0'
          };
        });
      }
      
      return {
        id: ideaId,
        name: metrics?.itemName || 'Unknown Item',
        mean: metrics?.mean?.toFixed(1) || 'N/A',
        median: metrics?.median?.toFixed(1) || 'N/A',
        min: metrics?.min || 'N/A',
        max: metrics?.max || 'N/A',
        stdDev: metrics?.std?.toFixed(2) || 'N/A',
        totalVotes: metrics?.totalVotes || 0,
        distribution: formattedDistribution
      };
    });
    
    // Sort items by average rating (descending)
    ratedItems.sort((a, b) => {
      const aMean = parseFloat(a.mean) || 0;
      const bMean = parseFloat(b.mean) || 0;
      return bMean - aMean;
    });
    
    console.log('📊 Processed multiple rating data:', { 
      itemCount: ratedItems.length,
      sampleItem: ratedItems[0],
      timestamp: new Date().toISOString() 
    });
  }
    
    // Extract rating items data with enhanced extraction logic
    let ratingItems = [];
    
    // Try ideas path (most common)
    if (stats?.summary?.ideas && Array.isArray(stats.summary.ideas)) {
      console.log('📊 Extracting rating items from stats.summary.ideas');
      ratingItems = stats.summary.ideas.map(idea => ({
        title: idea.title || 'Unnamed Item',
        averageRating: idea.avg_rating || 0,
        highRatingCount: idea.high_rating_count || 0,
        highRatingPercentage: idea.high_rating_freq ? Math.round(idea.high_rating_freq * 100) : 0,
        lowRatingCount: idea.low_rating_count || 0,
        lowRatingPercentage: idea.low_rating_freq ? Math.round(idea.low_rating_freq * 100) : 0,
        distribution: idea.distribution || [],
        standardDeviation: idea.std_dev || 0,
        engagement: idea.engagement_score || idea.like_count || 0,
        comments: idea.comment_count || 0
      }));
    } 
    // Try winning_ideas path
    else if (stats?.summary?.winning_ideas && Array.isArray(stats.summary.winning_ideas)) {
      console.log('📊 Extracting rating items from stats.summary.winning_ideas');
      ratingItems = stats.summary.winning_ideas.map(idea => ({
        title: idea.title || 'Unnamed Item',
        averageRating: idea.avg_rating || 0,
        highRatingCount: idea.high_rating_count || 0,
        highRatingPercentage: idea.high_rating_freq ? Math.round(idea.high_rating_freq * 100) : 0,
        lowRatingCount: idea.low_rating_count || 0,
        lowRatingPercentage: idea.low_rating_freq ? Math.round(idea.low_rating_freq * 100) : 0,
        distribution: idea.distribution || [],
        standardDeviation: idea.std_dev || 0,
        engagement: idea.engagement_score || idea.like_count || 0,
        comments: idea.comment_count || 0
      }));
    }    // Try rating_items path if present
    else if (stats?.summary?.rating_items && Array.isArray(stats.summary.rating_items)) {
      console.log('📊 Extracting rating items from stats.summary.rating_items');
      ratingItems = stats.summary.rating_items.map(item => ({
        title: item.title || item.name || 'Unnamed Item',
        averageRating: item.average_rating || item.avg_rating || 0,
        highRatingCount: item.high_count || item.high_rating_count || 0,
        highRatingPercentage: item.high_percentage || item.high_rating_percentage || 0,
        lowRatingCount: item.low_count || item.low_rating_count || 0,
        lowRatingPercentage: item.low_percentage || item.low_rating_percentage || 0,
        distribution: item.distribution || [],
        standardDeviation: item.standard_deviation || item.std_dev || 0,
        engagement: item.engagement || item.engagement_score || item.like_count || 0,
        comments: item.comments || item.comment_count || 0
      }));
    }    // Try items path if present (generic fallback)
    else if (stats?.summary?.items && Array.isArray(stats.summary.items)) {
      console.log('📊 Extracting rating items from stats.summary.items');
      ratingItems = stats.summary.items.map(item => ({
        title: item.title || item.name || 'Unnamed Item',
        averageRating: item.rating || item.average_rating || item.avg_rating || 0,
        highRatingCount: item.high_count || 0,
        highRatingPercentage: item.high_percentage || 0,
        lowRatingCount: item.low_count || 0,
        lowRatingPercentage: item.low_percentage || 0,
        distribution: item.distribution || [],
        standardDeviation: item.std_dev || 0,
        engagement: item.engagement || 0,
        comments: item.comments || 0
      }));
    }    
    console.log('📊 Final rating items extraction:', {
      ratingItemsCount: ratingItems.length,
      ratingItemsSample: ratingItems.slice(0, 3), // Log first few items as sample
      timestamp: new Date().toISOString()
    });
    
    // Sort items by average rating (highest first)
    ratingItems.sort((a, b) => b.averageRating - a.averageRating);
    
    // Cross-validate totalResponses with combined ratings data
    // If totalResponses is 0 but we have rating items, try to calculate from their data
    if (totalResponses === 0 && ratingItems.length > 0) {
      let totalCount = 0;
      
      // Try to sum up all high/low counts to get a response count
      ratingItems.forEach(item => {
        if (item.highRatingCount > 0 || item.lowRatingCount > 0) {
          totalCount += (item.highRatingCount + item.lowRatingCount);
        }      });
      
      // Only update if we found some data
      if (totalCount > 0) {
        // We need to divide by number of items since each user would rate multiple items
        if (ratingItems.length > 0) {
          totalCount = Math.round(totalCount / ratingItems.length);
        }        
        totalResponses = totalCount;
        console.log('📊 Recalculated totalResponses from rating items data:', {
          totalCount,
          newTotalResponses: totalResponses,
          timestamp: new Date().toISOString()
        });
        
        // Update participation rate with new total responses
        if (totalUsers > 0) {
          participationRate = Math.round((totalResponses / totalUsers) * 100);
        }      }    }    
    // Extract user segment data
    const userSegments = {
      basicUsers: stats?.metrics?.basic_user || 0,
      heroUsers: stats?.metrics?.hero_user || 0,
      earlyAccessUsers: stats?.metrics?.early_access_user || 0,
      observerUsers: stats?.metrics?.observer_user || 0
    };
    
    // Extract platform distribution
    const platformDistribution = {
      desktop: stats?.metrics?.desktop_user || 0,
      ios: stats?.metrics?.ios_user || 0,
      android: stats?.metrics?.android_user || 0,
      mobileWeb: stats?.metrics?.mobile_web_user || 0
    };
    
    // Calculate engagement metrics with enhanced extraction logic
    // Try multiple paths for likes
    let totalLikes = stats?.metrics?.comment_likes;
    if (totalLikes === undefined || totalLikes === null) {
      totalLikes = stats?.metrics?.likes;
    }    if (totalLikes === undefined || totalLikes === null) {
      totalLikes = stats?.summary?.total_likes;
    }    totalLikes = totalLikes || 0;

    // Apply same approach for dislikes
    let totalDislikes = stats?.metrics?.comment_dislikes;
    if (totalDislikes === undefined || totalDislikes === null) {
      totalDislikes = stats?.metrics?.dislikes;
    }    totalDislikes = totalDislikes || 0;
    
    // Apply same approach for comments
    let totalComments = stats?.metrics?.comments;
    if (totalComments === undefined || totalComments === null) {
      totalComments = stats?.metrics?.comment_count;
    }    totalComments = totalComments || 0;
    
    // Calculate engagement rate as the percentage of responses that received engagement
    // First determine how many responses received engagement (without double-counting)
    let engagementRate = 0;
    
    if (totalResponses > 0) {
      // Method 1: If there are likes or comments, calculate what percentage of responses received engagement
      if (totalLikes > 0 || totalComments > 0) {
        // Estimate engaged responses (can't be more than total responses)
        const estimatedEngagedResponses = Math.min(
          totalResponses, // Can't be more than total responses
          Math.ceil((totalLikes + totalComments) / 3) // Estimate assuming avg of 3 engagements per response
        );
        engagementRate = Math.round((estimatedEngagedResponses / totalResponses) * 100);
      }    }      
    console.log('📊 Multiple rating engagement metrics:', {
      totalLikes,
      totalDislikes,
      totalComments,
      totalResponses,
      totalUsers,
      engagementRate,
      calculationMethod: 'estimated percentage of engaged responses',
      timestamp: new Date().toISOString()
    });
    
    // Analyze rating patterns
    let ratingPatterns = {
      highestRatedItem: ratingItems.length > 0 ? ratingItems[0].title : 'N/A',
      lowestRatedItem: ratingItems.length > 0 ? ratingItems[ratingItems.length - 1].title : 'N/A',
      averageRatingSpread: 0,
      ratingConsistency: 'Unknown',
      polarizingItems: []
    };
    
    if (ratingItems.length > 0) {
      // Calculate rating spread (difference between highest and lowest average ratings)
      const highestAvg = ratingItems[0].averageRating;
      const lowestAvg = ratingItems[ratingItems.length - 1].averageRating;
      ratingPatterns.averageRatingSpread = Math.round((highestAvg - lowestAvg) * 10) / 10;
      
      // Calculate average standard deviation across all items
      const avgStdDev = ratingItems.reduce((sum, item) => sum + (item.standardDeviation || 0), 0) / ratingItems.length;
      
      // Determine rating consistency based on average standard deviation
      if (avgStdDev < 0.8) {
        ratingPatterns.ratingConsistency = 'Very Consistent';
      } else if (avgStdDev < 1.2) {
        ratingPatterns.ratingConsistency = 'Mostly Consistent';
      } else if (avgStdDev < 1.6) {
        ratingPatterns.ratingConsistency = 'Somewhat Varied';
      } else {
        ratingPatterns.ratingConsistency = 'Highly Varied';
      }      
      // Identify polarizing items (high counts in both high and low ratings)
      ratingPatterns.polarizingItems = ratingItems
        .filter(item => item.highRatingPercentage > 20 && item.lowRatingPercentage > 20)
        .map(item => item.title);
    }    
    // Format rated items for prompt
    let formattedRatingItems = '';
    if (ratingItems && ratingItems.length > 0) {
      formattedRatingItems = ratingItems.map((item, idx) =>
        `${idx + 1}. ${item.title}: Avg Rating ${item.averageRating} - Top Rank: ${item.highRatingPercentage}% - Bottom Rank: ${item.lowRatingPercentage}% - Engagement: ${item.engagement}`
      ).join('\n');
    } else {
      formattedRatingItems = 'No rating item data available.';
    }    
    // Format polarization data
    const polarizationText = ratingPatterns.polarizingItems.length > 0 
      ? ratingPatterns.polarizingItems.join(', ')
      : 'None detected';
    
    // Format user comments to provide context for ratings
    let formattedComments = '';
    if (Array.isArray(posts) && posts.length > 0) {
      // Sort posts by engagement (likes + comments)
      const sortedPosts = [...posts].sort((a, b) => {
        const aEngagement = (a?.like_count || 0) + (a?.child_count || 0);
        const bEngagement = (b?.like_count || 0) + (b?.child_count || 0);
        return bEngagement - aEngagement; // Descending order
      });
      
      formattedComments = sortedPosts.map(post => {
        const content = post?.content || '';
        
        // Format replies if they exist
        const replies = Array.isArray(post?.children) 
          ? post.children.map(child => {
              return `  - Reply: "${child?.content || ''}"'`;
            }).join('\n')
          : '';
        
        return `"${content}"${replies ? '\n' + replies : ''}`;
      }).join('\n\n');
    } else {
      formattedComments = 'No comments available';
    }
    
    // Use the cached rating data if available, otherwise fall back to the basic rating items
    const useRichRatingData = ratedItems.length > 0;
    
    // Format the overall statistics section
    const statsLines = [
      `Total Responses: ${totalResponses}`, 
      participationRate ? `Participation Rate: ${participationRate}%` : null, 
      totalComments > 0 ? `Total Comments: ${totalComments}` : null
    ].filter(Boolean).join('\n    ');
    
    // Format the rated items section with enhanced data if available
    let ratingItemsSection = '';
    
    if (useRichRatingData) {
      // Using detailed cached data
      ratingItemsSection = '\nRated Items (Detailed):'
        + ratedItems.map((item, index) => {
          // Format the distribution data for this item
          const distributionText = Object.entries(item.distribution || {})
            .sort(([ratingA], [ratingB]) => parseInt(ratingB) - parseInt(ratingA)) // Sort by rating (5 to 1)
            .map(([rating, data]) => `      ${rating} Stars: ${data.percentage}% (${data.count} votes)`)
            .join('\n');
          
          return `\n  ${index + 1}. ${item.name}\n`
            + `    Average Rating: ${item.mean}\n`
            + `    Median: ${item.median}\n`
            + `    Range: ${item.min} - ${item.max}\n`
            + `    Standard Deviation: ${item.stdDev}\n`
            + `    Total Votes: ${item.totalVotes}\n`
            + `    Distribution:\n${distributionText}`;
        }).join('');
    } else if (ratingItems.length > 0) {
      // Fallback to basic data
      ratingItemsSection = `\nRated Items:\n    ${ratingItems.map(item => `${item.title || 'Item'}: ${item.averageRating || 'N/A'}`).join('\n    ')}`;
    }
    
    // Assemble the complete data section
    const formattedData = `
Event Details:
- Name: ${eventName}
- Question Type: ${questionType}
- Description: ${description}

Participation Statistics:
    ${statsLines}${ratingItemsSection}

User Comments:
${formattedComments}`;

// Initialize the prompt template - ensuring we only use real available data
console.log('📚 Final verification of multiple rating prompt data integrity');
console.log('📊 Only using real, available data points - no estimations');
    
// Create multiple rating prompt template
const promptTemplate = `# Multiple Rating Analysis Task

You are an expert data analyst analyzing a Multiple Rating survey. This type of survey asks respondents to rate multiple items on a numerical scale. Analyze the following data and provide a structured report in markdown format:

## Event Analysis Data
${formattedData}

## Analysis Request
Provide a comprehensive analysis of this multiple rating exercise. Consider patterns, trends, and insights across all rated items. Structure your analysis with these sections:

# Executive Summary
- [Provide a concise 3-5 sentence overview of the key findings]
- [Highlight the most significant insights from the rating data]
- [Summarize overall participant sentiment and trends]
- [Note any standout patterns or trends across multiple rated items]

# Key Themes
- [Identify 3-5 major themes from the rating data]
- [Focus on comparative patterns across items]
- [Include both strengths and areas for improvement]
- [Highlight differences in how items were rated]
- [Analyze distribution patterns across ratings]

# Recommendations
- [Provide 3-5 actionable next steps based on the rating data]
- [Include specific suggestions for addressing lower-rated items]
- [Recommend strategies for leveraging higher-rated items]
- [Suggest how to address any polarized ratings if present]
- [Offer ideas for follow-up actions to gain deeper insights]
- [Suggest follow-up questions to explore identified themes further]

# Limitations
- [List any limitations in the data that affect the depth or breadth of analysis]
- [Suggest what additional data would be helpful]
- [Note any potential biases or gaps in the responses]

# Survey Design Feedback
- [Provide suggestions for improving the survey question or format]
- [Suggest how to generate richer, more actionable, or more diverse responses]

Format your response with these exact section headings using single # for each section. Ensure each section has substantive content.`;

console.log('✅ Multiple rating prompt generated:', {
  promptLength: promptTemplate.length,
  timestamp: new Date().toISOString()
});

return promptTemplate;
} catch (error) {
  console.error(' Error in generateMultipleRatingPrompt:', {
    error: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString()
  });
  return null;
}
}